import {
  Icon,
  Input,
  InputProps,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { SxProps, SystemStyleObject } from '@mui/system';
import { BaseComment, Comment, ConductType, SimpleListResult } from '@schooly/api';
import { ConductEntryPatch, ConductTypeRepresentation, ConductVisibility } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { CellDropdownSelect, DropdownSelectRow } from '@schooly/components/filters';
import { DropdownSelect } from '@schooly/components/filters';
import {
  ArchiveIcon,
  ArrowDownV2Icon,
  CommentRowCell,
  CrossIcon,
  DropdownComments,
} from '@schooly/style';
import { FC, useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useResizeDetector } from 'react-resize-detector';

import PersonCardBasic from '../../../components/common/PersonCard/PersonCardBasic';
import { DropdownOption } from '../../../components/uikit-components/Dropdown/Dropdown';
import {
  Grid,
  GridBody,
  GridCell,
  GridContainer,
  GridRow,
} from '../../../components/uikit-components/Grid/Grid';
import { useWithRef } from '../../../hooks/useWithRef';
import { commentToDropdownCommentItem } from '../../../utils/convertComments';
import { ConductVisibilityButton } from '../ConductVisibilityButton';
const MIN_ROW_HEIGHT = 53;
const TITLE_CELL_WIDTH = 160;

export interface ConductManageGridProps {
  selectedConductType?: ConductType;
  selectConductTypes?: ConductType[];
  selectedStudents: SimpleListResult[];
  conductEntryPatches: Record<string, ConductEntryPatch>;
  setConductEntryPatches: (v: Record<string, ConductEntryPatch>) => void;
  errors?: Record<string, boolean> | null;
  onChange?(studentRelationId?: string): void;
}

function GhostCellInput({ padding, ...props }: InputProps & { padding?: string }) {
  return (
    <Input
      disableUnderline
      fullWidth
      maxRows={10}
      sx={(theme) => ({
        padding: padding ?? theme.spacing(1),
        height: '100%',
        display: 'flex',
        alignItems: 'flex-start',

        '&.Mui-focused': {
          background: theme.palette.background.paper,
        },
      })}
      {...props}
    />
  );
}

export const ConductManageGrid: FC<ConductManageGridProps> = ({
  errors,
  onChange,
  selectedStudents,
  selectedConductType,
  selectConductTypes,
  setConductEntryPatches,
  conductEntryPatches,
}) => {
  const { currentStaff } = useAuth();

  const dropdownOptions = useMemo(() => {
    const options =
      selectConductTypes?.find((type) => type.id === selectedConductType?.id)
        ?.select_type_options ?? [];
    const optionsSorted = options.sort((a, b) => a.order - b.order);

    return optionsSorted.reduce<DropdownOption<string>[]>((acc, option) => {
      if (option.archived) return acc;
      acc.push({ label: option.label, value: option.label });
      return acc;
    }, []);
  }, [selectConductTypes, selectedConductType]);

  const setEntryComment = useCallback<
    (entryId: string, relationId?: string, comment?: string) => void
  >(
    async (studentId, relationId, comment = '') => {
      if (!currentStaff) {
        return;
      }

      const comments: BaseComment[] = [...(conductEntryPatches[studentId]?.comments ?? [])];

      const existingCommentIndex = relationId
        ? (comments.findIndex((comment) => comment.creator_relation_id === relationId) ?? -1)
        : -1;

      const existingComment =
        existingCommentIndex >= 0 ? comments[existingCommentIndex] : undefined;

      if (existingComment && existingComment.comment === comment) {
        return;
      }

      const tmpComment: BaseComment = existingComment
        ? { ...existingComment, comment }
        : {
            comment,
            creator_relation_id: currentStaff.relation_id,
            creator_title: currentStaff.title,
            creator_given_name: currentStaff.given_name,
            creator_last_name: currentStaff.last_name,
            creator_known_as: currentStaff.known_as,
          };

      if (existingCommentIndex >= 0) {
        comments[existingCommentIndex]! = tmpComment;
      } else {
        comments.push(tmpComment);
      }

      setConductEntryPatches({
        ...conductEntryPatches,
        [studentId]: { ...conductEntryPatches[studentId], comments: comments as Comment[] },
      });
    },
    [setConductEntryPatches, conductEntryPatches, currentStaff],
  );

  const handleCommentAdd = useCallback(
    (studentId: string) => (comment: string) => {
      if (comment) {
        setEntryComment(studentId, undefined, comment);
      }
    },
    [setEntryComment],
  );

  const handleCommentEdit = useCallback(
    (studentId: string) => (comment: string, relationId: string) => {
      setEntryComment(studentId, relationId, comment);
    },
    [setEntryComment],
  );

  const handleConductVisibility = useCallback(
    (studentId: string, currentVisibility: ConductVisibility) => {
      const visibility =
        currentVisibility === ConductVisibility.NOT_PUBLISHED
          ? ConductVisibility.PUBLISHED
          : ConductVisibility.NOT_PUBLISHED;

      setConductEntryPatches({
        ...conductEntryPatches,
        [studentId]: { ...conductEntryPatches[studentId], visibility },
      });
    },
    [setConductEntryPatches, conductEntryPatches],
  );

  const updateHandler = useCallback(
    (relationIdToUpdate: string, value: Partial<ConductEntryPatch>) => {
      const updated = {
        ...conductEntryPatches,
        [relationIdToUpdate]: {
          relation_id: relationIdToUpdate,
          ...conductEntryPatches[relationIdToUpdate],
          ...value,
        },
      };

      setConductEntryPatches(updated);
      onChange?.(relationIdToUpdate);
    },
    [setConductEntryPatches, conductEntryPatches, onChange],
  );

  return (
    <GridContainer sx={{ px: 2.5 }}>
      <Grid
        sx={{
          height: 1, // hack to take cell's child full height (GhostCellInput)
          borderCollapse: 'collapse',
        }}
      >
        <GridBody>
          {selectedStudents.map((student, i) => {
            const conductEntryPatch = conductEntryPatches[student.relation_id];

            const showBorderTop = i === 0;
            const showBorderBottom = i === selectedStudents.length - 1;

            return (
              <ConductManageGridRow
                key={student.relation_id}
                student={student}
                errors={errors}
                sx={(theme) => ({
                  '&:hover': {
                    // TODO: textarea::placeholder is not activated
                    '.ConductManageGrid__cell__detail textarea::placeholder, .UIKit-DropdownComments__icon':
                      {
                        color: theme.palette.common.grey2,
                        opacity: 1,
                      },
                    '.dropdownContainer': {
                      '.MuiTypography-root': {
                        color: conductEntryPatch?.title
                          ? theme.palette.primary.main
                          : theme.palette.common.grey2,
                      },
                      '.arrowIcon': {
                        path: { stroke: theme.palette.common.grey2 },
                      },
                      '.archiveIcon': {
                        color: theme.palette.primary.main,
                      },
                    },
                  },
                })}
                showBorderTop={showBorderTop}
                showBorderBottom={showBorderBottom}
                handleConductVisibility={handleConductVisibility}
                conductEntryPatch={conductEntryPatch}
                handleUpdate={updateHandler}
                conductType={selectedConductType}
                dropdownOptions={dropdownOptions}
                handleCommentAdd={handleCommentAdd}
                handleCommentEdit={handleCommentEdit}
              />
            );
          })}
        </GridBody>
      </Grid>
    </GridContainer>
  );
};

type ConductManageGridRowProps = {
  sx?: SxProps<Theme>;
  student: SimpleListResult;
  errors?: Record<string, boolean> | null;
  showBorderTop: boolean;
  showBorderBottom: boolean;
  conductEntryPatch: ConductEntryPatch;
  conductType?: ConductType;
  dropdownOptions: DropdownOption[];
  handleConductVisibility: (studentId: string, currentVisibility: ConductVisibility) => void;
  handleUpdate: (relationIdToUpdate: string, value: Partial<ConductEntryPatch>) => void;
  handleCommentAdd: (studentId: string) => (comment: string) => void;
  handleCommentEdit: (studentId: string) => (comment: string, relationId: string) => void;
};

const ConductManageGridRow = ({
  sx,
  student,
  errors,
  showBorderTop,
  showBorderBottom,
  conductEntryPatch,
  conductType,
  dropdownOptions,
  handleConductVisibility,
  handleUpdate,
  handleCommentAdd,
  handleCommentEdit,
}: ConductManageGridRowProps) => {
  const theme = useTheme();
  const { currentStaff } = useAuth();
  const { formatMessage } = useIntl();
  const { getConfirmation } = useConfirmationDialog();

  const dropdownSelect = useRef<DropdownSelect | null>(null);
  const { containerRef } = useWithRef();

  const { ref, height } = useResizeDetector();
  const rowHeight = height ?? MIN_ROW_HEIGHT;

  const comments = conductEntryPatch?.comments?.map(commentToDropdownCommentItem) ?? [];
  const visibility = conductEntryPatch?.visibility ?? ConductVisibility.PUBLISHED;

  const archived = useMemo(() => {
    if (!conductType || conductType.type !== ConductTypeRepresentation.SELECT) return;
    return conductType.select_type_options?.find((o) => o.label === conductEntryPatch?.title)
      ?.archived;
  }, [conductEntryPatch?.title, conductType]);

  const getArchivedConfirmation = useCallback(async () => {
    return await getConfirmation({
      textId: 'deselect-conductOption-archived',
      textValues: { name: conductEntryPatch?.title ? `"${conductEntryPatch?.title}"` : undefined },
    });
  }, [conductEntryPatch?.title, getConfirmation]);

  const handleClearSelect = useCallback(async () => {
    if (archived) {
      const confirmed = await getArchivedConfirmation();
      if (!confirmed) return;
    }

    handleUpdate(student.relation_id, { title: null, value: null });
  }, [archived, getArchivedConfirmation, handleUpdate, student.relation_id]);

  const handleSelect = useCallback(
    async (title: string) => {
      if (archived) {
        const confirmed = await getArchivedConfirmation();
        if (!confirmed) return;
      }

      handleUpdate(student.relation_id, {
        title,
        value: null,
      });
      dropdownSelect.current?.close();
    },
    [archived, getArchivedConfirmation, handleUpdate, student.relation_id],
  );

  const renderSelectContent = useCallback(() => {
    const selectedLabel = conductEntryPatch?.title ?? '';

    return (
      <Stack gap={0.25} m={1}>
        {dropdownOptions.map((o) => (
          <DropdownSelectRow
            key={o.value}
            isSelected={selectedLabel === o.label}
            onClick={() => handleSelect(o.label)}
          >
            <Typography
              variant="h3"
              color={selectedLabel === o.label ? undefined : 'common.grey2'}
              sx={{ textWrap: 'wrap' }}
            >
              {o.label}
            </Typography>
          </DropdownSelectRow>
        ))}
      </Stack>
    );
  }, [conductEntryPatch?.title, dropdownOptions, handleSelect]);

  const renderSelectRightIcon = useCallback(
    (opened: boolean) => {
      if (opened) {
        return (
          <Icon onClick={handleClearSelect}>
            <CrossIcon />
          </Icon>
        );
      }

      return (
        <Icon
          className="arrowIcon"
          sx={(theme) => ({
            width: theme.spacing(1),
            transform: 'rotate(180deg)',
            path: { stroke: theme.palette.common.grey },
          })}
        >
          <ArrowDownV2Icon />
        </Icon>
      );
    },
    [handleClearSelect],
  );

  const renderSelectedOption = useCallback(() => {
    return (
      <Stack direction="row" gap={0.5} alignItems="center" overflow="hidden">
        {archived && (
          <Tooltip
            title={formatMessage({ id: 'conduct-OptionArchived' })}
            componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
          >
            <Icon
              className="archiveIcon"
              sx={(theme) => ({ color: theme.palette.common.grey2, zIndex: theme.zIndex.tooltip })}
            >
              <ArchiveIcon />
            </Icon>
          </Tooltip>
        )}
        <Typography color="text.primary" noWrap>
          {conductEntryPatch?.title ?? ''}
        </Typography>
      </Stack>
    );
  }, [archived, conductEntryPatch?.title, formatMessage]);

  const renderTitleCellContent = () => {
    switch (conductType?.type) {
      case ConductTypeRepresentation.SELECT:
        return (
          <CellDropdownSelect
            ref={dropdownSelect}
            emptyPlaceholder={formatMessage({ id: 'conduct-Type-Title' })}
            emptyLabelProps={{ sx: (theme) => theme.typography.body1 }}
            hasValues={!!conductEntryPatch?.title}
            renderRightIcon={renderSelectRightIcon}
            layoutStyleProps={{ paddingLeft: 1, paddingRight: 3, height: '100%' }}
            renderContent={renderSelectContent}
            sx={(theme) => ({
              maxWidth: TITLE_CELL_WIDTH - 1,
              height: `${rowHeight + 1}px`,
              transition: 'all .2s, height 0s',
              ' .right-icon': {
                right: theme.spacing(1),
                top: `calc(${rowHeight / 2}px - ${theme.spacing(1)})`,
              },
            })}
          >
            {renderSelectedOption}
          </CellDropdownSelect>
        );
      case ConductTypeRepresentation.TEXT:
        return (
          <GhostCellInput
            type="text"
            padding={theme.spacing(1.5, 1)}
            placeholder={formatMessage({ id: 'conduct-Type-Title' })}
            value={conductEntryPatch?.title}
            onChange={(e) =>
              handleUpdate(student.relation_id, { title: e.target.value, value: null })
            }
          />
        );
      case ConductTypeRepresentation.NUMBER:
        return (
          <GhostCellInput
            type="number"
            padding={theme.spacing(1.5, 1)}
            inputProps={{ min: 0 }}
            placeholder={formatMessage({ id: 'number' })}
            value={conductEntryPatch?.value}
            onChange={(e) =>
              handleUpdate(student.relation_id, { title: null, value: e.target.value })
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <GridRow
      ref={ref}
      key={student.user_id}
      error={errors?.[student.relation_id]}
      sx={(theme) => (typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject}
    >
      <GridCell
        borderTop={showBorderTop}
        borderBottom={showBorderBottom}
        borderRight
        sx={{ verticalAlign: 'top' }}
        width={44}
      >
        <ConductVisibilityButton
          onClick={() => handleConductVisibility(student.relation_id, visibility)}
          tooltipTitle={formatMessage({
            id:
              visibility === ConductVisibility.NOT_PUBLISHED
                ? 'conduct-stuff-visible-create'
                : 'conduct-parents-visible-create',
          })}
          selected={visibility === ConductVisibility.PUBLISHED}
          width={200}
        />
      </GridCell>
      <GridCell
        noVerticalPadding
        borderRight
        borderTop={showBorderTop}
        borderBottom={showBorderBottom}
        width={200}
        sx={{
          maxWidth: 400,
          verticalAlign: 'top',
          '&&': {
            pt: '3px',
          },
        }}
      >
        <PersonCardBasic userType="student" user={student} isListItem />
      </GridCell>

      {/* Value / Title */}
      <GridCell
        noPadding
        borderRight
        borderTop={showBorderTop}
        borderBottom={showBorderBottom}
        width={TITLE_CELL_WIDTH}
        sx={{ verticalAlign: 'top' }}
      >
        {renderTitleCellContent()}
      </GridCell>

      <GridCell
        noPadding
        borderRight
        borderTop={showBorderTop}
        borderBottom={showBorderBottom}
        width={500}
      >
        <GhostCellInput
          placeholder="Detail"
          multiline
          padding="17px 8px"
          className="ConductManageGrid__cell__detail"
          value={conductEntryPatch?.details}
          onChange={(e) => handleUpdate(student.relation_id, { details: e.target.value })}
        />
      </GridCell>

      <GridCell
        noVerticalPadding
        borderTop={showBorderTop}
        borderBottom={showBorderBottom}
        sx={{ verticalAlign: 'top' }}
      >
        <Stack py={1} justifyContent="center" alignItems="center">
          <CommentRowCell>
            {(onToggle, cellRef) => (
              <DropdownComments
                comments={comments}
                onAdd={handleCommentAdd(student.relation_id)}
                onEdit={handleCommentEdit(student.relation_id)}
                canAdd
                canEditOwn
                getParentRef={() => cellRef}
                onToggle={onToggle}
                popoverMargin={2}
                ref={containerRef}
                currentStaff={currentStaff}
              />
            )}
          </CommentRowCell>
        </Stack>
      </GridCell>
    </GridRow>
  );
};
